import { Link } from "react-router-dom";
import { ArrowLeft, PenLine } from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { DraftStepper } from "@/components/drafting/DraftStepper";
import { getDraftStepNav } from "@/lib/draft-step-nav";
import type { DraftMatter } from "@/types/drafting";

interface DraftContextHeaderProps {
  matter: DraftMatter;
  currentStep: number;
  backTo?: string;
  backTitle?: string;
}

export function DraftContextHeader({
  matter,
  currentStep,
  backTo,
  backTitle,
}: DraftContextHeaderProps) {
  const stepNav = getDraftStepNav(matter.id, currentStep, { matterStatus: matter.status });
  const resolvedBackTo = backTo ?? stepNav.previousHref ?? "/drafting";
  const resolvedBackTitle =
    backTitle ?? stepNav.previousLabel ?? "Back to drafts";

  return (
    <header className="min-w-0 border-b border-[var(--lm-border-subtle)] bg-[var(--bar-bg)] px-4 py-4 backdrop-blur-sm dark:border-slate-800/50 dark:bg-slate-900/80 md:px-6">
      <div className="mb-4 flex items-center gap-4">
        <IconButton asChild title={resolvedBackTitle}>
          <Link to={resolvedBackTo}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </IconButton>
        <div className="flex min-w-0 items-center gap-3">
          <div className="rounded-lg bg-violet-500/10 p-2">
            <PenLine className="h-5 w-5 text-violet-400" />
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
              {matter.title || matter.matter_number}
            </h1>
            <p className="truncate text-xs text-[var(--lm-muted)] dark:text-slate-500">
              {matter.matter_number}
              {matter.jurisdiction ? ` · ${matter.jurisdiction}` : ""}
            </p>
          </div>
        </div>
      </div>
      <DraftStepper currentStep={currentStep} matterId={matter.id} />
    </header>
  );
}
